/**
 * useDocuments Hook
 *
 * React Query hooks for fetching, uploading, and deleting entity documents.
 *
 * @see US-36.1 - Composants partagés documents
 * @see US-36.7 - Upload BL fournisseur
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsService } from '../services/documents.service';
import type { DocumentType } from '@bstock/shared';
import toast from 'react-hot-toast';

/**
 * Hook to fetch documents for a given entity
 * @param entityRoute - e.g. 'purchase-orders', 'goods-receipts', 'transfers'
 * @param entityId - UUID of the entity (query disabled when undefined)
 */
export function useEntityDocuments(entityRoute: string, entityId: string | undefined) {
  return useQuery({
    queryKey: ['documents', entityRoute, entityId],
    queryFn: () => documentsService.listByEntity(entityRoute, entityId!),
    enabled: !!entityId,
  });
}

/**
 * Hook to upload a document to an entity
 * @param entityRoute - e.g. 'purchase-orders', 'goods-receipts', 'transfers'
 * @param entityId - UUID of the entity
 */
export function useUploadDocument(entityRoute: string, entityId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType?: DocumentType }) =>
      documentsService.uploadDocument(entityRoute, entityId!, file, documentType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', entityRoute, entityId] });
      toast.success('Document uploade');
    },
    onError: () => {
      toast.error("Erreur lors de l'upload du document");
    },
  });
}

/**
 * Hook to delete a document from an entity
 * @param entityRoute - e.g. 'purchase-orders', 'goods-receipts', 'transfers'
 * @param entityId - UUID of the entity
 */
export function useDeleteDocument(entityRoute: string, entityId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) =>
      documentsService.deleteDocument(entityRoute, entityId!, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', entityRoute, entityId] });
      toast.success('Document supprime');
    },
    onError: () => {
      toast.error('Erreur lors de la suppression du document');
    },
  });
}
